import { config } from '@abyss/web/tools/config';
import enLangugages from '@uhg-psx/mapping-files-translation-transperfect/data/en/languageTranslations.json';
import { t } from 'i18next';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PersonalFit } from '../../../../../api/src/models/ProviderRecommendationDetails';
import { adobeLinkTrackEvent } from '../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../common/AdobeTagging/adobeModalTrackEvent';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoonMessage } from '../../../hooks/useLagoonMessage';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import {
  ProviderDetails,
  ProviderLocation,
  ProviderType,
} from '../../../models/ProviderDetails';
import {
  RecommendationCriteriaMatch,
  buildFaqUrl,
  getMatchDescription,
  getMatchText,
  removeSpecialCharactersUsingMatch,
} from '../../../utils/smartChoice.utils';
import { RecommendationProp } from '../../Widget/SmartChoiceWidget/PreferencesForm';
import { MatchText } from '../MatchText';
import { NotAMatchText } from '../NotAMatchText';
import {
  BreakdownContainer,
  ContainerWithTopandBottonBorder,
  CriteriaPointsContainer,
  CriteriaSubTitle,
  CriteriaTitle,
  DescriptionContainer,
  DrawerContentWithBackgroundgray,
  FAQContainer,
  FAQLink,
  StyledDescription,
} from '../SmartChoiceBreakDown.styles';
import { NotAMatchSearchIconAndText } from './Components/NotAMatchSearchIconAndText';
import {
  HealthGradeDescriptionContainer,
  StyledIcon,
  StyledLinkHealthGrade,
  StyledText,
} from './PersonalFitBreakDown.styles';

export interface PersonalFitBreakDownProps {
  personalFit: PersonalFit;
  preferencesData?: RecommendationProp;
  providerDetails?: ProviderDetails;
  locationKey?: string;
  chipBackgroundColor?: string;
}

type ReplacePlaceholders = (
  text: string | undefined,
  replacements: Record<string, string>
) => string;

type LanguageSectionProps = {
  isFacility: boolean;
  preferredLanguage: string | string[];
  personalFit: PersonalFit;
  isPreferredLanguage: boolean;
  allProviderLanguages: string[];
  languageNotSetFacilityMessage: string;
  spokenLanguagesUnknownMessage: string;
  speaksMessage?: string;
  doesNotSpeakMessage?: string;
  preferredLanguagesForMatch: string[];
  replacePlaceholders: ReplacePlaceholders;
};

type GenderSectionProps = {
  isFacility: boolean;
  preferredGender: string;
  providerGender: string;
  isPreferredGender: boolean;
  providerGenderUnknownMessage: string;
  providerIdentifiesAsMessage?: string;
  formattedProviderGender: string;
  replacePlaceholders: ReplacePlaceholders;
};

type BoardCertSectionProps = {
  isFacility: boolean;
  preferredBoardCert: string;
  isBoardCert: boolean;
  providerBoardCertifiedMessage?: string;
  providerBoardCertSpecialty: string[];
  notCertifiedMessage: string;
  replacePlaceholders: ReplacePlaceholders;
};

type PatientExperienceSectionProps = {
  isFacility: boolean;
  patientExperienceMessage?: string;
  personalFit: PersonalFit;
  consumerRatingAndStarsMessage?: string;
  consumerRatingMessage?: string;
  ratingUnavailableMessage: string;
  consumerRating: string;
  consumerRatingSurveyVolume: string;
  replacePlaceholders: ReplacePlaceholders;
};

function renderLanguageSection({
  isFacility,
  preferredLanguage,
  personalFit,
  isPreferredLanguage,
  allProviderLanguages,
  languageNotSetFacilityMessage,
  spokenLanguagesUnknownMessage,
  speaksMessage,
  doesNotSpeakMessage,
  preferredLanguagesForMatch,
  replacePlaceholders,
}: LanguageSectionProps) {
  if (!isFacility && preferredLanguage === '') {
    return null;
  }

  if (isFacility && preferredLanguage === '') {
    return <NotAMatchText text={languageNotSetFacilityMessage} />;
  }

  if (personalFit?.languageSpoken === 'unknown') {
    return <NotAMatchSearchIconAndText text={spokenLanguagesUnknownMessage} />;
  }

  if (isPreferredLanguage && allProviderLanguages?.length !== 0) {
    return (
      <MatchText
        text={replacePlaceholders(speaksMessage, {
          providerSpeaks: allProviderLanguages?.toString() || '',
        })}
      />
    );
  }

  return (
    <NotAMatchText
      text={replacePlaceholders(doesNotSpeakMessage, {
        providerSpeaks: preferredLanguagesForMatch.join(', '),
      })}
    />
  );
}

function renderGenderSection({
  isFacility,
  preferredGender,
  providerGender,
  isPreferredGender,
  providerGenderUnknownMessage,
  providerIdentifiesAsMessage,
  formattedProviderGender,
  replacePlaceholders,
}: GenderSectionProps) {
  if (isFacility || preferredGender === '') {
    return null;
  }

  if (providerGender === '' || providerGender?.toLowerCase() === 'unknown') {
    return <NotAMatchSearchIconAndText text={providerGenderUnknownMessage} />;
  }

  if (isPreferredGender) {
    return (
      <MatchText
        text={replacePlaceholders(providerIdentifiesAsMessage, {
          providerGender: t(formattedProviderGender),
        })}
      />
    );
  }

  return (
    <NotAMatchText
      text={replacePlaceholders(providerIdentifiesAsMessage, {
        providerGender: t(formattedProviderGender),
      })}
    />
  );
}

function renderBoardCertSection({
  isFacility,
  preferredBoardCert,
  isBoardCert,
  providerBoardCertifiedMessage,
  providerBoardCertSpecialty,
  notCertifiedMessage,
  replacePlaceholders,
}: BoardCertSectionProps) {
  if (isFacility || preferredBoardCert !== 'y') {
    return null;
  }

  if (isBoardCert) {
    return (
      <MatchText
        text={replacePlaceholders(providerBoardCertifiedMessage, {
          providerBoardCertifications: providerBoardCertSpecialty.join(', '),
        })}
      />
    );
  }

  return <NotAMatchText text={notCertifiedMessage} />;
}

function renderPatientExperienceSection({
  isFacility,
  patientExperienceMessage,
  personalFit,
  consumerRatingAndStarsMessage,
  consumerRatingMessage,
  ratingUnavailableMessage,
  consumerRating,
  consumerRatingSurveyVolume,
  replacePlaceholders,
}: PatientExperienceSectionProps) {
  if (isFacility) {
    return null;
  }

  const hasSurveyVolume =
    personalFit?.consumerRatingSurveyVolume !== '' &&
    personalFit?.consumerRatingSurveyVolume !== '0';
  const isMidPointMatch = personalFit?.consumerRatingMidPointStatus === 'Y';
  const isMidPointNotMatch = personalFit?.consumerRatingMidPointStatus === 'N';
  const hasRatingNoSurvey =
    (personalFit?.consumerRatingSurveyVolume === '' ||
      personalFit?.consumerRatingSurveyVolume === '0') &&
    consumerRating !== '';

  return (
    <>
      <CriteriaSubTitle data-testid="paitient-experiance">
        {patientExperienceMessage}
      </CriteriaSubTitle>

      {isMidPointMatch && hasSurveyVolume ? (
        <MatchText
          text={replacePlaceholders(consumerRatingAndStarsMessage, {
            consumerRating,
            consumerRatingSurveyVolume,
          })}
        />
      ) : isMidPointNotMatch && hasSurveyVolume ? (
        <NotAMatchText
          text={replacePlaceholders(consumerRatingAndStarsMessage, {
            consumerRating,
            consumerRatingSurveyVolume,
          })}
        />
      ) : hasRatingNoSurvey ? (
        <NotAMatchText
          text={replacePlaceholders(consumerRatingMessage, {
            consumerRating,
            consumerRatingSurveyVolume,
          })}
        />
      ) : (
        <NotAMatchSearchIconAndText text={ratingUnavailableMessage} />
      )}
    </>
  );
}

export const PersonalFitBreakDown = ({
  personalFit,
  providerDetails,
  preferencesData,
  locationKey,
  chipBackgroundColor,
}: PersonalFitBreakDownProps) => {
  const { portalData } = useContext(PortalContext);
  const isWidget = useStore(StoreKeys?.IS_WIDGET);
  const [matchCount, setMatchCount] = useState(0);
  const [notMatchCount, setNotMatchCount] = useState(0);
  const [consumerRating, setConsumerRating] = useState('');
  const [consumerRatingSurveyVolume, setConsumerRatingSurveyVolume] =
    useState('');

  useEffect(() => {
    adobeModalTrackEvent('Smart choice personal fit breakdown');
  }, []);
  const isUmr = isUmrMember(portalData?.portalName);
  const baseUrl = isUmr ? config('UMR_BASE_URL') : config('BASE_URL');
  const providerData = providerDetails as ProviderDetails;
  const isFacility = providerData?.providerType === ProviderType.ORGANIZATION;
  const providerLocationData: ProviderLocation =
    providerDetails?.providerLocations?.find((x) =>
      isFacility ? x.locationId === locationKey : x.locationKey === locationKey
    ) as ProviderLocation;
  const providerBoardCertSpecialty = providerData?.boardCertifications ?? [];
  const providerLanguages = providerData?.languagesSpoken ?? [];
  const providerLanguagesDefault =
    providerData?.defaultTranslatedValues?.languagesSpoken ?? [];
  const providerLanguagesByOffice =
    providerLocationData?.languagesSpokenByOffice ?? [];
  const providerLanguagesByOfficeDefault =
    providerLocationData?.defaultTranslatedValues?.languagesSpokenByOffice ??
    [];
  const allProviderLanguages = [
    ...new Set(
      providerLanguages
        ?.concat(providerLanguagesByOffice)
        ?.map((item) => removeSpecialCharactersUsingMatch(item ?? ''))
    ),
  ];
  const allProviderLanguagesForMatch = [
    ...new Set(
      providerLanguagesDefault
        ?.concat(providerLanguagesByOfficeDefault)

        ?.map((item) => removeSpecialCharactersUsingMatch(item ?? ''))
    ),
  ];
  const providerRating = providerData?.healthGradeRating ?? '';
  const providerGender = providerData?.gender ?? '';
  const preferredLanguage = preferencesData?.languagesSpoken ?? '';
  const preferredGender = preferencesData?.providerGender ?? '';
  const preferredBoardCert = preferencesData?.providerBoardCertStatus ?? '';
  const getFormattedProviderGender = providerGender?.replace(
    /(^\w{1})/g,
    (letter) => letter.toUpperCase()
  );

  const [enableTotalCostOfCareProgressBar] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TOTAL_COST_OF_CARE_PROGRESS_BAR,
  ]);

  const [searchParams] = useSearchParams();
  const chipValueFromParms = searchParams.get('chipValue');
  const chipValue = chipValueFromParms ?? '';

  const getFormattedPreferredLanguages =
    typeof preferredLanguage === 'string' &&
    preferredLanguage
      ?.split(',')
      ?.map((item) => enLangugages[item])
      ?.map((item) => removeSpecialCharactersUsingMatch(item));
  const preferredLanguagesForMatch = Array.isArray(
    getFormattedPreferredLanguages
  )
    ? getFormattedPreferredLanguages
    : [];
  const isPreferredLanguage =
    allProviderLanguagesForMatch?.length > 0 &&
    allProviderLanguagesForMatch?.some((lang) =>
      preferredLanguagesForMatch.includes(lang)
    );
  const isPreferredGender =
    providerGender !== '' &&
    preferredGender !== '' &&
    preferredGender
      ?.split(',')
      ?.some((item) => getFormattedProviderGender?.includes(item));
  const isBoardCert = providerBoardCertSpecialty?.length > 0;
  const getMatchCounts = (personalFit: PersonalFit) => {
    let matchCount = 0;
    let notMatchCount = 0;

    if (preferredLanguage !== '') {
      if (isPreferredLanguage && allProviderLanguages?.length !== 0) {
        matchCount++;
      } else {
        notMatchCount++;
      }
    }

    if (preferredGender !== '') {
      if (
        providerGender === '' ||
        providerGender?.toLowerCase() === 'unknown'
      ) {
        notMatchCount++;
      } else if (isPreferredGender) {
        matchCount++;
      } else {
        notMatchCount++;
      }
    }

    if (preferredBoardCert === 'y') {
      if (isBoardCert) {
        matchCount++;
      } else {
        notMatchCount++;
      }
    }

    if (
      Boolean(personalFit?.consumerRatingMidPointStatus === 'Y') &&
      personalFit?.consumerRatingSurveyVolume !== '' &&
      personalFit?.consumerRatingSurveyVolume !== '0'
    ) {
      matchCount++;
    } else if (
      personalFit?.consumerRatingMidPointStatus === 'N' &&
      personalFit?.consumerRatingSurveyVolume !== '' &&
      personalFit?.consumerRatingSurveyVolume !== '0'
    ) {
      notMatchCount++;
    } else if (
      (personalFit?.consumerRatingSurveyVolume === '' ||
        personalFit?.consumerRatingSurveyVolume === '0') &&
      consumerRating !== ''
    ) {
      notMatchCount++;
    } else {
      notMatchCount++;
    }

    return { matchCount, notMatchCount };
  };

  const replacePlaceholders = (
    text: string | undefined,
    replacements: Record<string, string>
  ): string =>
    (text || '').replace(/\{\{(.*?)}}/g, (_, key) => {
      const keys = key.split('.');
      let value: any = replacements;
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) break;
      }
      return typeof value === 'string' ? value : '';
    });

  const getMessage = useLagoonMessage({
    chipValue,
    providerType: providerDetails?.providerType || '',
  });

  const [
    description,
    compatibility_title,
    speaks,
    does_not_speak,
    spoken_languages_unknown,
    language_not_set_facility,
    provider_identifies_as,
    provider_gender_unknown,
    provider_board_certified,
    not_certified,
    paitient_experiance,
    consumer_rating_and_stars,
    consumer_rating,
    rating_unavailable,
    healthgrade_description,
    FAQs,
  ] = getMessage([
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_DESCRIPTION,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_COMPATABILITY_TITLE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_SPEAKS,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_DOES_NOT_SPEAKS_LANGUAGE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_LANGUAGE_UNKNOWN,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_LANGUAGE_NOT_SET_FACILITY,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_PROVIDER_IDENTIFIES_AS,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_GENDER_UNKNOWN,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_BOARD_CERTIFIED_IN,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_NOT_BORD_CERTIFIED,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_PAITIENT_EXPERIENCE_TITLE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_CONSIMER_RATING_STARS_ARE_PROVIDED_BY_HEALTHGRADES,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_CONSUMER_RATING,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_CONSUMER_RATING_INFO_UNAVAILABLE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_HEALTHGRADE_DESCRIPTION_AT_END,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BREAKDOWN_FAQ_TEXT,
  ]);

  useEffect(() => {
    const { matchCount, notMatchCount } = getMatchCounts(personalFit);
    setMatchCount(matchCount);
    setNotMatchCount(notMatchCount);
    if (personalFit?.consumerRating) {
      setConsumerRating(providerRating);
    }
    if (personalFit.consumerRatingSurveyVolume) {
      setConsumerRatingSurveyVolume(personalFit?.consumerRatingSurveyVolume);
    }
  }, [personalFit]);

  const outOf = t('OUT_OF');
  const outOfTotal = getMatchText(
    personalFit?.scoreDetails?.match as string,
    `(${matchCount} ${outOf} ${matchCount + notMatchCount})`
  );

  return (
    <BreakdownContainer data-testid="personal-fit-breakdown">
      <DrawerContentWithBackgroundgray
        css={
          chipBackgroundColor && enableTotalCostOfCareProgressBar
            ? { backgroundColor: chipBackgroundColor }
            : {}
        }
        direction="column"
      >
        <CriteriaTitle data-testid="match-description">
          {`${t(
            getMatchDescription(
              personalFit?.scoreDetails?.match ??
                RecommendationCriteriaMatch.None
            )
          )} ${outOfTotal}`}
        </CriteriaTitle>
      </DrawerContentWithBackgroundgray>
      <DescriptionContainer>
        <StyledDescription data-testid="personal-fit-description">
          {description?.message}
        </StyledDescription>
      </DescriptionContainer>
      <CriteriaPointsContainer data-testid="criteria-points" direction="column">
        {(isFacility ||
          preferredLanguage !== '' ||
          preferredGender !== '' ||
          preferredBoardCert === 'y') && (
          <CriteriaSubTitle data-testid="compatability-title">
            {compatibility_title?.message}
          </CriteriaSubTitle>
        )}

        {(isFacility || preferredLanguage !== '') &&
          renderLanguageSection({
            isFacility,
            preferredLanguage,
            personalFit,
            isPreferredLanguage,
            allProviderLanguages,
            languageNotSetFacilityMessage:
              language_not_set_facility?.message ?? '',
            spokenLanguagesUnknownMessage:
              spoken_languages_unknown?.message ?? '',
            speaksMessage: speaks?.message,
            doesNotSpeakMessage: does_not_speak?.message,
            preferredLanguagesForMatch,
            replacePlaceholders,
          })}
        {renderGenderSection({
          isFacility,
          preferredGender,
          providerGender,
          isPreferredGender,
          providerGenderUnknownMessage: provider_gender_unknown?.message ?? '',
          providerIdentifiesAsMessage: provider_identifies_as?.message,
          formattedProviderGender: getFormattedProviderGender,
          replacePlaceholders,
        })}

        {renderBoardCertSection({
          isFacility,
          preferredBoardCert,
          isBoardCert,
          providerBoardCertifiedMessage: provider_board_certified?.message,
          providerBoardCertSpecialty,
          notCertifiedMessage: not_certified?.message ?? '',
          replacePlaceholders,
        })}

        {renderPatientExperienceSection({
          isFacility,
          patientExperienceMessage: paitient_experiance?.message,
          personalFit,
          consumerRatingAndStarsMessage: consumer_rating_and_stars?.message,
          consumerRatingMessage: consumer_rating?.message,
          ratingUnavailableMessage: rating_unavailable?.message ?? '',
          consumerRating,
          consumerRatingSurveyVolume,
          replacePlaceholders,
        })}
      </CriteriaPointsContainer>
      <ContainerWithTopandBottonBorder
        data-testid="healthgrades-container"
        direction="column"
      >
        <StyledIcon
          icon="healthgrades"
          size={132}
          title={t('HEALTH_GRADES_LOGO_TITLE')}
        />
        <HealthGradeDescriptionContainer data-testid="healthgrades-description">
          <StyledText>{healthgrade_description?.message}</StyledText>
          <StyledLinkHealthGrade
            data-testid="healthgrades-link"
            href="https://www.healthgrades.com"
            onClick={() =>
              adobeLinkTrackEvent({
                name: 'healthgrade',
                location: 'Smart choice personal fit breakdown',
              })
            }
            openNewWindow
          >
            {''}
          </StyledLinkHealthGrade>
        </HealthGradeDescriptionContainer>
      </ContainerWithTopandBottonBorder>
      <FAQContainer>
        <FAQLink
          data-testid="faq-link"
          href={buildFaqUrl(baseUrl, isWidget)}
          onClick={() => {
            adobeLinkTrackEvent({
              name: 'Faqs',
              location: 'Smart choice personal fit breakdown',
            });
          }}
        >
          {FAQs?.message}
        </FAQLink>
      </FAQContainer>
    </BreakdownContainer>
  );
};
